/**
 * <cpap-price-beat-form> — Price Beat claim form (multi-item, 2026-07).
 *
 * Three render contexts:
 *
 *   1. Standalone page (/pages/price-beat-claim):
 *      Search-first picker — customer types a name/SKU (Shopify predictive
 *      search) or pastes a CDW product link into the same box. Picking a
 *      result fetches /products/<handle>.js and renders a "selected product
 *      card" with a size/variant dropdown.
 *
 *   2. PDP modal (cpap-price-beat-modal.liquid):
 *      data-product-handle (+ data-variant-id) pre-set on the element by the
 *      host snippet; the search input is omitted by Liquid and the card
 *      renders locked (variant still switchable).
 *
 *   3. Prefill (?product=<handle>&variant=<id> on the standalone page):
 *      The PDP trust line appends the handle + currently-selected variant, so
 *      the card is pre-rendered with the right size — changeable.
 *
 * Add-ons: "Add anything else to your order" rows — same picker per row with
 * variant dropdown + qty. Submitted as items[] = [{variant_id, qty, role}]
 * with exactly one role='claim'. The hidden product_handle input stays in
 * sync so the payload also works against the legacy single-product backend.
 *
 * Submit POSTs JSON to data-submit-url. On 202, success state shows claim_id.
 */

(() => {
  const tagName = 'cpap-price-beat-form';
  if (customElements.get(tagName)) return;

  const MAX_EXTRAS = 5;
  const MAX_QTY = 10;

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c]));
  }
  function escapeAttr(s) { return escapeHtml(s); }
  function money(cents) { return `A$${(Number(cents) / 100).toFixed(2)}`; }

  // Extracts a handle from "https://cpapdiscount.au/products/<handle>" (and variants).
  function parseHandle(url) {
    if (!url) return '';
    try {
      const u = new URL(url, 'https://cpapdiscount.au');
      const m = u.pathname.match(/\/products\/([a-z0-9][a-z0-9-]{0,255})/i);
      return m ? m[1].toLowerCase() : '';
    } catch {
      const m = String(url).match(/\/products\/([a-z0-9][a-z0-9-]{0,255})/i);
      return m ? m[1].toLowerCase() : '';
    }
  }

  // Extracts ?variant=123 from a pasted PDP link so we can preselect the size.
  function parseVariantParam(url) {
    try {
      const u = new URL(url, 'https://cpapdiscount.au');
      const v = u.searchParams.get('variant');
      return v && /^\d+$/.test(v) ? v : '';
    } catch { return ''; }
  }

  /**
   * Search-result weighting group: machines (0) → masks (1) → everything
   * else (2). Shopify's predictive search ranks purely on text relevance, so
   * "AirSense 11 Standard Filter" outranks the AirSense 11 machine — but most
   * price-beat claims are machines or masks. Product type is authoritative
   * ("CPAP Machine", "Machine Device", "Devices" / "CPAP Mask", "Nasal
   * pillows mask" in this store — note "Device Filters" must NOT match);
   * title keywords are the fallback when type is missing.
   */
  function searchGroup(p) {
    const type = String(p.type || '').toLowerCase();
    if (type) {
      if (/\bmachine\b/.test(type) || type === 'devices') return 0;
      if (/\bmask\b/.test(type)) return 1;
      return 2;
    }
    const title = String(p.title || '').toLowerCase();
    if (/\bmachine\b/.test(title)) return 0;
    if (/\bmask\b/.test(title)) return 1;
    return 2;
  }

  // Stable re-rank: group, then in-stock first within the group, then
  // Shopify's own relevance order.
  function weightSearchResults(products) {
    return products
      .map((p, i) => ({ p, i, g: searchGroup(p), avail: p.available ? 0 : 1 }))
      .sort((a, b) => (a.g - b.g) || (a.avail - b.avail) || (a.i - b.i))
      .map((x) => x.p);
  }

  class CpapPriceBeatForm extends HTMLElement {
    connectedCallback() {
      this.form               = this.querySelector('[ref="form"]');
      this.productHandleInput = this.querySelector('[ref="productHandleInput"]');
      this.productUrlInput    = this.querySelector('[ref="productUrlInput"]');
      this.claimSearchWrap    = this.querySelector('[ref="claimSearchWrap"]');
      this.claimSearchInput   = this.querySelector('[ref="claimSearchInput"]');
      this.claimSearchResults = this.querySelector('[ref="claimSearchResults"]');
      this.claimHint          = this.querySelector('[ref="claimHint"]');
      this.claimCardSlot      = this.querySelector('[ref="claimCardSlot"]');
      this.extrasList         = this.querySelector('[ref="extrasList"]');
      this.addExtraButton     = this.querySelector('[ref="addExtraButton"]');
      this.submitButton       = this.querySelector('[ref="submitButton"]');
      this.spinner            = this.querySelector('[ref="spinner"]');
      this.successState       = this.querySelector('[ref="successState"]');
      this.errorState         = this.querySelector('[ref="errorState"]');
      this.errorMessage       = this.querySelector('[ref="errorMessage"]');
      this.claimIdDisplay     = this.querySelector('[ref="claimIdDisplay"]');

      this.submitUrl           = this.dataset.submitUrl;
      this.statusUrlBase       = this.dataset.statusUrlBase;
      this.lockedProductHandle = this.dataset.productHandle;
      this.lockedVariantId     = this.dataset.variantId || '';

      this.productCache = new Map();   // handle -> /products/<handle>.js JSON
      this.claim = null;               // { product, variantId }
      this.extraData = new Map();      // rowEl -> { product, variantId, qty }

      // Brick-mortar source toggle — radio swap between URL and business-name fields.
      this.sourceOnlineRadio   = this.querySelector('[ref="sourceOnlineRadio"]');
      this.sourceInStoreRadio  = this.querySelector('[ref="sourceInStoreRadio"]');
      this.sourceOnlineRow     = this.querySelector('[ref="sourceOnlineRow"]');
      this.sourceInStoreRow    = this.querySelector('[ref="sourceInStoreRow"]');
      this.competitorUrlInput  = this.querySelector('[ref="competitorUrlInput"]');
      this.competitorNameInput = this.querySelector('[ref="competitorNameInput"]');
      if (this.sourceOnlineRadio && this.sourceInStoreRadio) {
        this.sourceOnlineRadio.addEventListener('change', () => this.applySource('online'));
        this.sourceInStoreRadio.addEventListener('change', () => this.applySource('in_store'));
        this.applySource(this.sourceInStoreRadio.checked ? 'in_store' : 'online');
      }

      // Claim product picker.
      if (this.lockedProductHandle) {
        // PDP-modal context: product locked; render the card (variant still switchable).
        this.productHandleInput.value = this.lockedProductHandle;
        this.pickClaim(this.lockedProductHandle, this.lockedVariantId, { locked: true });
      } else if (this.claimSearchInput) {
        this.attachSearch(this.claimSearchInput, this.claimSearchResults, (handle, variantId) => {
          this.pickClaim(handle, variantId);
        });
        // Prefill from the PDP trust line: ?product=<handle>&variant=<id>
        const params = new URLSearchParams(window.location.search);
        const prefillHandle = (params.get('product') || '').toLowerCase();
        const prefillVariant = params.get('variant') || '';
        if (/^[a-z0-9][a-z0-9-]{0,255}$/.test(prefillHandle)) {
          this.pickClaim(prefillHandle, /^\d+$/.test(prefillVariant) ? prefillVariant : '');
        }
      }

      // Add-on rows.
      if (this.addExtraButton) {
        this.addExtraButton.addEventListener('click', () => this.addExtraRow());
      }

      // Auto-prepend https:// on URL inputs so customers can paste "wtran.net/test"
      // without hitting "Please enter a URL" from the browser's type=url validator.
      this.querySelectorAll('input[type="url"]').forEach((inp) => {
        inp.addEventListener('blur', () => this.normalizeUrl(inp));
      });

      // The terms link lives inside the consent <label>; make sure clicking it
      // only opens the policy tab and never doubles as a checkbox toggle.
      const termsLink = this.querySelector('[ref="termsLink"]');
      if (termsLink) {
        termsLink.addEventListener('click', (e) => e.stopPropagation());
      }

      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    normalizeUrl(inp) {
      const v = (inp.value || '').trim();
      if (v && !/^https?:\/\//i.test(v)) {
        inp.value = 'https://' + v;
      }
    }

    /**
     * Toggle between online-URL and in-store written-quote modes.
     * Swap visibility AND the `required` attribute so the browser's native
     * "please fill this field" stops nagging about the hidden row.
     */
    applySource(mode) {
      const isOnline = mode === 'online';
      if (this.sourceOnlineRow)  this.sourceOnlineRow.hidden  = !isOnline;
      if (this.sourceInStoreRow) this.sourceInStoreRow.hidden =  isOnline;
      if (this.competitorUrlInput) {
        this.competitorUrlInput.required = isOnline;
        if (!isOnline) this.competitorUrlInput.value = '';
      }
      if (this.competitorNameInput) {
        this.competitorNameInput.required = !isOnline;
        if (isOnline) this.competitorNameInput.value = '';
      }
    }

    // -----------------------------------------------------------------------
    // Product search (shared by the claim picker and every add-on row)
    // -----------------------------------------------------------------------

    attachSearch(input, resultsEl, onPick) {
      let timer;
      input.addEventListener('input', () => {
        clearTimeout(timer);
        const raw = input.value.trim();
        // Pasted CDW product link → resolve directly, preserving ?variant=.
        if (raw.includes('/products/')) {
          const handle = parseHandle(raw);
          if (handle) {
            resultsEl.hidden = true;
            onPick(handle, parseVariantParam(raw));
            return;
          }
        }
        if (raw.length < 2) { resultsEl.hidden = true; return; }
        timer = setTimeout(() => this.runSearch(raw, resultsEl, onPick), 240);
      });
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { resultsEl.hidden = true; input.blur(); }
        // Don't let Enter submit the form from a search box mid-pick.
        if (e.key === 'Enter') e.preventDefault();
      });
      document.addEventListener('click', (e) => {
        const wrap = input.closest('.cpb-search-wrap');
        if (wrap && !wrap.contains(e.target)) resultsEl.hidden = true;
      });
    }

    async runSearch(q, resultsEl, onPick) {
      let products = [];
      try {
        const u = `/search/suggest.json?q=${encodeURIComponent(q)}` +
                  `&resources[type]=product` +
                  `&resources[limit]=10` +
                  `&resources[options][unavailable_products]=last` +
                  `&resources[options][fields]=title,vendor,product_type,variants.title,variants.sku`;
        const res = await fetch(u, { headers: { Accept: 'application/json' } });
        if (res.ok) {
          const data = await res.json();
          products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        }
      } catch { /* render empty state below */ }
      this.renderSearchResults(weightSearchResults(products), resultsEl, onPick);
    }

    renderSearchResults(products, resultsEl, onPick) {
      if (!products.length) {
        resultsEl.innerHTML = '<li class="cpb-search-empty">No products found.</li>';
        resultsEl.hidden = false;
        return;
      }
      resultsEl.innerHTML = products.map((p) => {
        const img = p.image || (p.featured_image && p.featured_image.url) || '';
        const price = p.price != null ? `A$${Number(p.price).toFixed(2)}` : '';
        const avail = p.available ? 'In stock' : 'Out of stock';
        return `
          <li class="cpb-search-result" data-handle="${escapeAttr(p.handle)}">
            <img class="cpb-search-result-img" src="${escapeAttr(img)}" alt="" loading="lazy" onerror="this.style.visibility='hidden'">
            <div class="cpb-search-result-body">
              <div class="cpb-search-result-title">${escapeHtml(p.title)}</div>
              <div class="cpb-search-result-meta">${escapeHtml(avail)}${price ? ' · ' + price : ''}</div>
            </div>
          </li>`;
      }).join('');
      resultsEl.hidden = false;
      for (const li of resultsEl.querySelectorAll('.cpb-search-result')) {
        li.addEventListener('click', () => {
          resultsEl.hidden = true;
          onPick(li.dataset.handle, '');
        });
      }
    }

    // -----------------------------------------------------------------------
    // Product JSON + card rendering
    // -----------------------------------------------------------------------

    async fetchProduct(handle) {
      if (this.productCache.has(handle)) return this.productCache.get(handle);
      const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`product fetch ${res.status}`);
      const p = await res.json();
      this.productCache.set(handle, p);
      return p;
    }

    resolveVariantId(product, wantedId) {
      const variants = product.variants || [];
      if (wantedId && variants.some((v) => String(v.id) === String(wantedId))) return String(wantedId);
      const firstAvailable = variants.find((v) => v.available);
      return String((firstAvailable || variants[0] || {}).id || '');
    }

    productImage(product, variant) {
      let src = (variant && variant.featured_image && variant.featured_image.src)
        || (product.images && product.images[0])
        || product.featured_image
        || '';
      if (src && src.startsWith('//')) src = 'https:' + src;
      return src;
    }

    variantById(product, variantId) {
      return (product.variants || []).find((v) => String(v.id) === String(variantId)) || null;
    }

    /**
     * Shared card markup. opts: { locked, qty } — qty != null renders the
     * add-on quantity stepper; locked hides the change/remove control.
     */
    cardHtml(product, variantId, opts) {
      const variant = this.variantById(product, variantId);
      const img = this.productImage(product, variant);
      const variants = product.variants || [];
      const multi = variants.length > 1;
      const options = variants.map((v) => `
        <option value="${escapeAttr(String(v.id))}"${String(v.id) === String(variantId) ? ' selected' : ''}>
          ${escapeHtml(v.title)}${v.available ? '' : ' — out of stock'}
        </option>`).join('');
      const sku = variant && variant.sku ? `<span class="cpb-card-sku">SKU ${escapeHtml(variant.sku)}</span>` : '';
      return `
        <div class="cpb-card">
          <img class="cpb-card-img" src="${escapeAttr(img)}" alt="" loading="lazy" onerror="this.style.visibility='hidden'">
          <div class="cpb-card-body">
            <div class="cpb-card-title">${escapeHtml(product.title)}</div>
            <div class="cpb-card-meta">
              ${multi ? `<select class="cpb-card-variant cpb-form-input" data-card-variant aria-label="Size / variant">${options}</select>` : ''}
              <span class="cpb-card-price" data-card-price>${variant ? money(variant.price) : ''}</span>
              ${sku}
            </div>
          </div>
          <div class="cpb-card-actions">
            ${opts && opts.qty != null ? `
              <label class="cpb-card-qty-label">Qty
                <input type="number" class="cpb-card-qty cpb-form-input" data-card-qty min="1" max="${MAX_QTY}" step="1" value="${opts.qty}" inputmode="numeric" aria-label="Quantity">
              </label>` : ''}
            ${opts && opts.locked ? '' : `<button type="button" class="cpb-card-remove" data-card-remove aria-label="${opts && opts.qty != null ? 'Remove this product' : 'Change product'}">${opts && opts.qty != null ? '✕' : 'Change'}</button>`}
          </div>
        </div>`;
    }

    // -----------------------------------------------------------------------
    // Claim product
    // -----------------------------------------------------------------------

    async pickClaim(handle, variantId, opts) {
      const locked = !!(opts && opts.locked);
      try {
        const product = await this.fetchProduct(handle);
        this.claim = { product, variantId: this.resolveVariantId(product, variantId) };
        this.renderClaimCard(locked);
        this.syncClaimHiddenInputs();
      } catch {
        if (locked) return; // modal fallback: hidden handle still submits legacy-style
        if (this.claimHint) {
          this.claimHint.textContent = "We couldn't load that product — please try searching again.";
        }
      }
    }

    renderClaimCard(locked) {
      if (!this.claimCardSlot || !this.claim) return;
      this.claimCardSlot.innerHTML = this.cardHtml(this.claim.product, this.claim.variantId, { locked });
      this.claimCardSlot.hidden = false;
      if (this.claimSearchWrap) this.claimSearchWrap.hidden = true;
      if (this.claimHint) this.claimHint.hidden = true;

      const select = this.claimCardSlot.querySelector('[data-card-variant]');
      if (select) {
        select.addEventListener('change', () => {
          this.claim.variantId = select.value;
          const v = this.variantById(this.claim.product, select.value);
          const priceEl = this.claimCardSlot.querySelector('[data-card-price]');
          if (priceEl && v) priceEl.textContent = money(v.price);
          this.syncClaimHiddenInputs();
        });
      }
      const change = this.claimCardSlot.querySelector('[data-card-remove]');
      if (change) {
        change.addEventListener('click', () => {
          this.claim = null;
          this.claimCardSlot.hidden = true;
          this.claimCardSlot.innerHTML = '';
          if (this.claimSearchWrap) this.claimSearchWrap.hidden = false;
          if (this.claimHint) {
            this.claimHint.hidden = false;
            this.claimHint.textContent = 'Start typing to see suggestions — or paste a cpapdiscount.au product link.';
          }
          this.syncClaimHiddenInputs();
          if (this.claimSearchInput) {
            this.claimSearchInput.value = '';
            this.claimSearchInput.focus();
          }
        });
      }
    }

    syncClaimHiddenInputs() {
      const handle = this.claim ? this.claim.product.handle : (this.lockedProductHandle || '');
      if (this.productHandleInput) this.productHandleInput.value = handle;
      if (this.productUrlInput) this.productUrlInput.value = handle ? `https://cpapdiscount.au/products/${handle}` : '';
    }

    // -----------------------------------------------------------------------
    // Add-on rows
    // -----------------------------------------------------------------------

    addExtraRow() {
      if (this.extrasList.children.length >= MAX_EXTRAS) return;
      const row = document.createElement('div');
      row.className = 'cpb-extra-row';
      row.innerHTML = `
        <div class="cpb-search-wrap">
          <input type="search" autocomplete="off" class="cpb-form-input" placeholder="Search by name or SKU — or paste a link">
          <ul class="cpb-search-results" hidden></ul>
        </div>
        <button type="button" class="cpb-card-remove" data-row-remove aria-label="Remove this row">✕</button>`;
      this.extrasList.appendChild(row);
      this.updateAddExtraState();

      const input = row.querySelector('input[type="search"]');
      const results = row.querySelector('.cpb-search-results');
      this.attachSearch(input, results, (handle, variantId) => this.pickExtra(row, handle, variantId));
      row.querySelector('[data-row-remove]').addEventListener('click', () => this.removeExtraRow(row));
      input.focus();
    }

    async pickExtra(row, handle, variantId) {
      try {
        const product = await this.fetchProduct(handle);
        const data = { product, variantId: this.resolveVariantId(product, variantId), qty: 1 };
        this.extraData.set(row, data);
        this.renderExtraCard(row);
      } catch {
        const input = row.querySelector('input[type="search"]');
        if (input) {
          input.setCustomValidity("We couldn't load that product — please try again.");
          input.reportValidity();
          setTimeout(() => input.setCustomValidity(''), 2500);
        }
      }
    }

    renderExtraCard(row) {
      const data = this.extraData.get(row);
      if (!data) return;
      row.innerHTML = this.cardHtml(data.product, data.variantId, { qty: data.qty });
      const select = row.querySelector('[data-card-variant]');
      if (select) {
        select.addEventListener('change', () => {
          data.variantId = select.value;
          const v = this.variantById(data.product, select.value);
          const priceEl = row.querySelector('[data-card-price]');
          if (priceEl && v) priceEl.textContent = money(v.price);
        });
      }
      const qtyInput = row.querySelector('[data-card-qty]');
      if (qtyInput) {
        qtyInput.addEventListener('change', () => {
          let q = Math.round(Number(qtyInput.value));
          if (!Number.isFinite(q) || q < 1) q = 1;
          if (q > MAX_QTY) q = MAX_QTY;
          qtyInput.value = q;
          data.qty = q;
        });
      }
      row.querySelector('[data-card-remove]').addEventListener('click', () => this.removeExtraRow(row));
    }

    removeExtraRow(row) {
      this.extraData.delete(row);
      row.remove();
      this.updateAddExtraState();
    }

    updateAddExtraState() {
      if (this.addExtraButton) {
        this.addExtraButton.hidden = this.extrasList.children.length >= MAX_EXTRAS;
      }
    }

    // -----------------------------------------------------------------------
    // Submit
    // -----------------------------------------------------------------------

    onSubmit(e) {
      e.preventDefault();
      // Normalize all URL fields one last time — covers cases where the user
      // hits Submit without blurring (e.g. tab-to-submit, mobile keyboard "Go").
      this.querySelectorAll('input[type="url"]').forEach((inp) => this.normalizeUrl(inp));
      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }

      // Claim product must be picked (locked context always has a handle even
      // if the card render failed — that submits legacy-style, single item).
      if (!this.claim && !this.lockedProductHandle) {
        this.showError('Please pick the product you found cheaper — start typing in the product field and choose a suggestion, or paste the cpapdiscount.au link.');
        if (this.claimSearchInput) this.claimSearchInput.focus();
        return;
      }

      // Unfinished add-on rows: empty ones are dropped silently; rows with
      // text but no picked product block the submit (dropping them would lose
      // something the customer clearly wanted).
      for (const row of [...this.extrasList.children]) {
        if (this.extraData.has(row)) continue;
        const input = row.querySelector('input[type="search"]');
        const text = input ? input.value.trim() : '';
        if (!text) { this.removeExtraRow(row); continue; }
        this.showError(`Finish choosing "${text}" from the suggestions — or remove that row.`);
        input.focus();
        return;
      }

      this.submitPayload();
    }

    async submitPayload() {
      this.setSubmitting(true);
      const formData = new FormData(this.form);
      const isInStore = this.sourceInStoreRadio && this.sourceInStoreRadio.checked;

      // items[]: claim first (qty locked to 1 — the offer price is per unit),
      // then add-ons. Omitted entirely when the card never rendered (locked
      // fallback) so the backend takes the legacy single-product path.
      let items;
      if (this.claim) {
        items = [{ variant_id: String(this.claim.variantId), qty: 1, role: 'claim' }];
        for (const data of this.extraData.values()) {
          items.push({ variant_id: String(data.variantId), qty: data.qty, role: 'addon' });
        }
      }

      const payload = {
        product_handle:   formData.get('product_handle'),
        product_url:      formData.get('product_url') || undefined,
        items,
        competitor_url:   isInStore ? undefined : (formData.get('competitor_url') || undefined),
        competitor_name:  isInStore ? (formData.get('competitor_name') || undefined) : undefined,
        claimed_price:    parseFloat(formData.get('claimed_price')),
        customer_email:   formData.get('customer_email'),
        customer_phone:   formData.get('customer_phone') || undefined,
        customer_notes:   formData.get('customer_notes') || undefined,
        confirm:          formData.get('confirm') === 'on',
        marketing_opt_in: formData.get('confirm') === 'on',
        source:           this.lockedProductHandle ? 'pdp_modal' : 'standalone_page',
      };

      try {
        const res = await fetch(this.submitUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const text = await res.text();
        let data;
        try { data = JSON.parse(text); } catch { data = { error: text }; }

        if (!res.ok) {
          const fieldMsg = data.errors && data.errors[0] && data.errors[0].message;
          this.showError(fieldMsg || data.message || data.error || `Submit failed (${res.status})`);
          this.setSubmitting(false);
          return;
        }
        this.showSuccess(data.claim_id);
      } catch (err) {
        this.showError('Network error. Please try again, or email contact@cpapdiscount.au.');
        this.setSubmitting(false);
      }
    }

    setSubmitting(on) {
      if (this.submitButton) this.submitButton.disabled = on;
      if (this.spinner)      this.spinner.hidden = !on;
    }

    showSuccess(claim_id) {
      this.form.hidden = true;
      if (this.errorState) this.errorState.hidden = true;
      if (this.claimIdDisplay) {
        // Display uppercase for visual punch; storage in Shopify stays lowercase.
        this.claimIdDisplay.textContent = (claim_id || '—').toUpperCase();
      }
      if (this.successState) {
        this.successState.hidden = false;
        this.successState.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    showError(msg) {
      if (this.errorMessage) this.errorMessage.textContent = msg;
      if (this.errorState) {
        this.errorState.hidden = false;
        this.errorState.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  customElements.define(tagName, CpapPriceBeatForm);
})();
